import { WelcomePanelState } from 'typings/reduxStore'
import { ActiveAccount } from 'typings/demographic'
import {
  PayloadAction,
  createDraftSafeSelector,
  createSelector,
  createSlice
} from '@reduxjs/toolkit'
import type { OriginalRootState } from 'redux/rootReducer'

export const initState: WelcomePanelState = {
  activeAccountInfo: {} as ActiveAccount,
  demographyInfo: [],
  errorMessage: '',
  isResponseFail: false,
  privilegeUpdateStatus: ''
}

const demographicSlice = createSlice({
  name: 'demographicSlice',
  initialState: initState,
  reducers: {
    updateDemographicInfoState: (state, action) => {
      return {
        ...state,
        ...action.payload
      }
    },
    setActiveAccount: (state, action) => {
      return {
        ...state,
        activeAccountInfo: {
          ...state.activeAccountInfo,
          ...action.payload
        }
      }
    },
    /*
     * Ideally, this action should be moved to extra reducer. However, it will
     * create a dependency cycle. For the time being, this action must be
     * dispatched by the login thunk
     */
    loginSuccessful: (state, { payload }: PayloadAction<ActiveAccount>) => {
      state.activeAccountInfo.accountID = payload.accountID
      state.activeAccountInfo.accountNumber = payload.accountNumber
    }
  }
})

export const { updateDemographicInfoState, setActiveAccount, loginSuccessful } =
  demographicSlice.actions

/** Base selector. Returns this slice. */
const selectSelf = (state: OriginalRootState) => state.welcomePanel

export const selectActiveAccountInfo = (state: OriginalRootState) =>
  state.welcomePanel.activeAccountInfo

/** Returns the active account number token string from the store */
export const selectAccountNumber = (state: OriginalRootState): string =>
  selectActiveAccountInfo(state).accountNumber

/**
 * Returns either the logged in parent account id or the active account id when
 * a prox account has subsequently switched to a child account.
 */
export const selectActiveAccountId = createSelector(
  selectSelf,
  state => state.activeAccountInfo.accountID
)

const selectDemographyInfo = (state: OriginalRootState) =>
  state.welcomePanel.demographyInfo

export const selectAvailableAccounts = (
  state: OriginalRootState
): ActiveAccount[] => {
  const activeAccountInfo = selectActiveAccountInfo(state)

  const isParentAccount =
    activeAccountInfo?.relationship?.toUpperCase() === 'PARENT'

  return isParentAccount ? selectDemographyInfo(state) : [activeAccountInfo]
}

/**
 * Returns all child accounts (relationship is not "PARENT")
 */
export const selectChildAccounts = createDraftSafeSelector(
  selectDemographyInfo,
  state => state.filter(account => account.relationship !== 'PARENT')
)

export default demographicSlice.reducer
